import * as React from 'react'
import { connect } from 'react-redux'

import * as commonActions from 'common/actions'
import { OfferOverflowList } from 'embed/constants'
import { DepositsOffer } from 'embed/constants/models'
import {
  DepositProductTypesOptions,
  DepositsSortOption,
  OfferType,
  Product,
  ProductResponseKeys,
  QueryParamKeys,
  RedirectType,
  SortDirection,
} from 'even-ts-static'

import ErrorIndicator from 'embed/components/ErrorIndicator'
import LoadingIndicator, { BRAND_COLOR } from 'embed/components/LoadingIndicator'
import PaginateOffers from 'embed/components/PaginateOffers'

import DepositsOfferList from './index'
import { MultiProductState } from 'embed/reducers'
import { depositsSortedSavingsOffersSelector } from 'embed/selectors/rates'
import { uiViewportHeightSelector, uiViewportWidthSelector, useCompactListItemSelector } from 'embed/selectors/ui'

interface OwnProps {
  offerStyleIsCompact: boolean
}

export interface Props {
  availableDepositsOffersTotal: number
  depositsOffers: DepositsOffer[]
  dispatchClearFilters?: () => null
  dispatchRedirectToOffer?: (offer: DepositsOffer) => null
  dispatchSortChanged?: (sortOption: DepositsSortOption, sortDirection: SortDirection) => null
  error: boolean
  limit?: number
  loading?: number
  pageSize?: number
  offerUuid?: string
  offerOverflow?: OfferOverflowList
  sortDirection: SortDirection
  sortOption: DepositsSortOption
  useCompactListItem?: boolean
  viewportHeight?: number
  viewportWidth?: number
}

const DepositsOfferListContainer: React.FC<Props> = ({
  availableDepositsOffersTotal,
  dispatchClearFilters,
  dispatchRedirectToOffer,
  error,
  limit,
  loading,
  depositsOffers,
  offerOverflow,
  pageSize,
  sortOption,
  sortDirection,
  dispatchSortChanged,
  useCompactListItem,
  ...props
}) => {
  const offerListRef = React.useRef(null)

  const onSortOptionClick = React.useCallback(
    (updatedSortOption: DepositsSortOption) => {
      if (updatedSortOption === sortOption) {
        // If they clicked the same sort, reverse it
        const updatedSortDirection = sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc
        dispatchSortChanged?.(updatedSortOption, updatedSortDirection)
      } else {
        dispatchSortChanged?.(updatedSortOption, SortDirection.Asc)
      }
    },
    [sortOption, sortDirection, dispatchSortChanged],
  )

  if (error) {
    return <ErrorIndicator />
  } else if (loading) {
    return <LoadingIndicator brandColor={BRAND_COLOR.TRUE} loadingLabel="Finding Offers" product={Product.Deposits} />
  } else {
    return (
      <PaginateOffers
        enabled={offerOverflow === OfferOverflowList.PAGINATE}
        offers={depositsOffers.slice(0, limit)}
        offerListRef={offerListRef}
        pageSize={pageSize}
      >
        {({ offersPage, paginationControls }) => (
          <>
            <DepositsOfferList
              availableDepositsOffersTotal={availableDepositsOffersTotal}
              bestOfferId={depositsOffers[0]?.uuid}
              depositsOffers={(offersPage ?? []) as DepositsOffer[]}
              depositsOffersTotal={depositsOffers.length}
              dispatchClearFilters={dispatchClearFilters}
              dispatchOfferCta={dispatchRedirectToOffer}
              offerListRef={offerListRef}
              onSortOptionClick={onSortOptionClick}
              sort={sortOption}
              sortDirection={sortDirection}
              useCompactListItem={!!useCompactListItem}
              {...props}
            />
            {paginationControls}
          </>
        )}
      </PaginateOffers>
    )
  }
}

const mapStateToProps = (state: MultiProductState, ownProps) => {
  let depositsOffers = ownProps.depositsOffers ?? depositsSortedSavingsOffersSelector(state)

  // Get the raw number of deposits offers available (regardless of filtering). We'll use this to determine which error message
  // to show: if there are no offers at all vs there are offers but you've applied a filter that filtered them all out
  const availableDepositsOffersTotal = (
    state.rates[Product.Deposits]?.[`${ProductResponseKeys[Product.Deposits]}Offers`] ?? []
  ).length

  // hide offers with unknown productSubTypes
  depositsOffers = depositsOffers.filter((offer) =>
    Object.values(DepositProductTypesOptions).includes(offer.productSubType),
  )

  const viewportHeight = uiViewportHeightSelector(state)
  const viewportWidth = uiViewportWidthSelector(state)
  const useCompactListItem = useCompactListItemSelector(state)

  return {
    availableDepositsOffersTotal,
    depositsOffers,
    error: state.loading[Product.Deposits]?.error,
    loading: state.loading[Product.Deposits]?.loading,
    offerOverflow: state.config[QueryParamKeys.OfferOverflow],
    offerUuid: state.rates[Product.Deposits].uuid,
    pageSize: state.config[QueryParamKeys.PageSize],
    sortDirection: state.rates.sortDirection[Product.Deposits],
    sortOption: state.rates.sortOption[Product.Deposits],
    useCompactListItem,
    viewportHeight,
    viewportWidth,
  }
}

const mapDispatchToProps = (dispatch, ownProps: OwnProps) => ({
  dispatchClearFilters: () => dispatch(commonActions.clearFilters(Product.Deposits)),
  dispatchRedirectToOffer: (offer: DepositsOffer) =>
    dispatch(
      commonActions.redirectToOffer(offer, OfferType.Deposits, RedirectType.MANUAL, {
        openInNewTab: ownProps.offerStyleIsCompact,
      }),
    ),
  dispatchSortChanged: (sortOption, sortDirection) =>
    dispatch(commonActions.sortChanged(sortDirection, Product.Deposits, sortOption)),
})

export default connect(mapStateToProps, mapDispatchToProps)(DepositsOfferListContainer)
